import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Save, CheckCircle2, RefreshCw } from "lucide-react";
import api from "@/lib/api";

function ProfileSEO() {
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [data, setData] = useState(null);
  const [editedDescription, setEditedDescription] = useState("");

  const handleOptimize = async () => {
    setLoading(true);
    try {
      const res = await api.post("/google/optimize-profile");
      if (res.data.success) {
        setData(res.data);
        setEditedDescription(res.data.aiResult?.optimizedDescription || "");
        toast.success("Profile analyzed and optimized!");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to optimize profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!editedDescription.trim()) return toast.error("Description cannot be empty");
    if (editedDescription.length > 750) return toast.error("Description must be under 750 characters");
    
    setPublishing(true);
    try {
      const res = await api.post("/google/update-profile", { description: editedDescription });
      if (res.data.success) {
        toast.success("Successfully published to Google Business Profile!");
        setData(prev => ({
          ...prev,
          currentDescription: editedDescription,
          aiResult: null
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to publish to Google");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profile SEO Optimizer</h1>
        <p className="text-muted-foreground mt-1">
          Use AI to inject high-ranking keywords into your Google Business Profile to boost local search rankings.
        </p>
      </div>

      {!data ? (
        <div className="bg-white border rounded-xl p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles size={32} />
          </div>
          <h2 className="text-xl font-semibold mb-2">Optimize Your Business Profile</h2>
          <p className="text-muted-foreground max-w-md mx-auto mb-8">
            Our AI will analyze your current Google Business Profile, identify your primary category, and rewrite your description with proven local SEO keywords to help you rank higher on Google Maps.
          </p>
          <button
            onClick={handleOptimize}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? <RefreshCw className="animate-spin" size={20} /> : <Sparkles size={20} />}
            {loading ? "Analyzing Profile..." : "Analyze & Optimize Now"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-700">Current Description</h3>
                <span className="text-xs text-gray-500">{data.currentDescription.length} / 750</span>
              </div>
              <div className="p-6">
                {data.currentDescription ? (
                  <p className="text-gray-600 whitespace-pre-wrap">{data.currentDescription}</p>
                ) : (
                  <p className="text-gray-400 italic">No description found on your Google Business Profile.</p>
                )}
              </div>
            </div>

            {data.aiResult && (
              <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                <div className="bg-blue-50/50 px-6 py-4 border-b">
                  <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-blue-600" />
                    AI SEO Analysis
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">Changes Made</span>
                    <p className="text-gray-700 mt-1">{data.aiResult.changesMade}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2 block">High-Value Keywords Injected</span>
                    <div className="flex flex-wrap gap-2">
                      {data.aiResult.keywordsUsed?.map((kw, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white border-2 border-blue-100 rounded-xl overflow-hidden shadow-sm relative flex flex-col">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-bl-full -z-10" />
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100 flex items-center justify-between">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <Sparkles size={18} className="text-blue-600" />
                Optimized SEO Description
              </h3>
              <span className={`text-xs font-medium ${editedDescription.length > 750 ? 'text-red-500' : 'text-blue-600'}`}>
                {editedDescription.length} / 750 max
              </span>
            </div>
            
            <div className="p-6 flex-1 flex flex-col">
              <p className="text-sm text-gray-500 mb-4">
                Review the AI-generated description below. You can edit it if needed before publishing.
              </p>
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className="w-full flex-1 min-h-[200px] p-4 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y text-gray-700 leading-relaxed"
                placeholder="Your optimized business description..."
              />

              <div className="mt-6 flex items-center gap-4">
                <button
                  onClick={handlePublish}
                  disabled={publishing || editedDescription.length > 750 || !editedDescription.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {publishing ? <RefreshCw className="animate-spin" size={20} /> : <Save size={20} />}
                  {publishing ? "Publishing..." : "Publish to Google Maps"}
                </button>
                <button
                  onClick={handleOptimize}
                  disabled={loading || publishing}
                  className="px-4 py-3 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center disabled:opacity-50"
                  title="Regenerate"
                >
                  <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileSEO;
